import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useCallback, useEffect, useState } from "react";
import { api, type MusicianItem, type SongDetail } from "./api";
import { musiciansQuery, songQuery } from "./queries";

/**
 * Edit-mode state for the song detail page: title/notes/author form
 * fields, debounced preview, and the staged recording order.
 *
 * Recording deletions and reordering are staged in `recordingOrder` and
 * only persisted on save (the server deletes recordings omitted from
 * the order). Cancelling discards the staged changes.
 */
export function useSongEditor(id: string) {
  const queryClient = useQueryClient();
  const [isEditing, setIsEditing] = useState(false);
  const [title, setTitle] = useState("");
  const [notes, setNotes] = useState("");
  const [authorId, setAuthorId] = useState<string | null>(null);
  const [debouncedNotes, setDebouncedNotes] = useState("");
  const [isSaving, setIsSaving] = useState(false);
  const [recordingOrder, setRecordingOrder] = useState<string[]>([]);
  // The order as saved on the server, so an untouched order isn't sent (and
  // can't delete a recording another client added meanwhile).
  const [savedRecordingOrder, setSavedRecordingOrder] = useState<string[]>([]);

  // Debounce preview to avoid re-rendering ChordPro on every keystroke
  useEffect(() => {
    if (!isEditing) return;
    const timeout = setTimeout(() => {
      setDebouncedNotes(notes);
    }, 150);
    return () => clearTimeout(timeout);
  }, [notes, isEditing]);

  const startEditing = (song: SongDetail) => {
    setTitle(song.title);
    setNotes(song.notes || "");
    setAuthorId(song.authorId ?? null);
    setDebouncedNotes(song.notes || "");
    const ids = song.recordings.map((r) => r.id);
    setRecordingOrder(ids);
    setSavedRecordingOrder(ids);
    setIsEditing(true);
  };

  const save = async () => {
    setIsSaving(true);
    try {
      const res = await api.songs[":id"].$patch({
        param: { id },
        json: {
          title,
          notes,
          authorId: authorId ?? undefined,
          recordingOrder: sameIds(recordingOrder, savedRecordingOrder)
            ? undefined
            : recordingOrder,
        },
      });
      if (!res.ok) throw new Error("Failed to save");
      await queryClient.invalidateQueries({
        queryKey: songQuery(id).queryKey,
      });
      setIsEditing(false);
    } catch (error) {
      console.error("Failed to save:", error);
    } finally {
      setIsSaving(false);
    }
  };

  const cancel = () => setIsEditing(false);

  const removeRecording = (recordingId: string) =>
    setRecordingOrder((prev) => prev.filter((rid) => rid !== recordingId));

  const reorderRecordings = (newOrder: string[]) => setRecordingOrder(newOrder);

  /** Keep a recording uploaded mid-edit when the staged order is saved. */
  const appendRecording = (recordingId: string) => {
    if (isEditing) {
      setRecordingOrder((prev) => [...prev, recordingId]);
      setSavedRecordingOrder((prev) => [...prev, recordingId]);
    }
  };

  const { data: musicians = [] } = useQuery({
    ...musiciansQuery(),
    enabled: isEditing,
  });

  const createMusician = useCallback(
    async (name: string, email?: string): Promise<MusicianItem> => {
      const res = await api.musicians.$post({ json: { name, email } });
      if (!res.ok) throw new Error("Failed to create musician");
      const created = await res.json();
      await queryClient.invalidateQueries({
        queryKey: musiciansQuery().queryKey,
      });
      return created as MusicianItem;
    },
    [queryClient],
  );

  return {
    isEditing,
    title,
    setTitle,
    notes,
    setNotes,
    authorId,
    setAuthorId,
    debouncedNotes,
    isSaving,
    recordingOrder,
    startEditing,
    save,
    cancel,
    removeRecording,
    reorderRecordings,
    appendRecording,
    musicians,
    createMusician,
  };
}

const sameIds = (a: string[], b: string[]) =>
  a.length === b.length && a.every((id, i) => id === b[i]);
